import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { trendlineStrategyService } from '../../../config/api';
import { submitAsyncBacktest } from '../../../services/backtestService';
import BacktestProgressModal from '../../Backtest/BacktestProgressModal';

// Execution mode constants
const EXECUTION_MODES = {
  LIVE: { value: 'LIVE', label: 'Live', color: 'btn-error', title: 'Launch Live Trading', isBacktest: false },
  PAPER: { value: 'PAPER', label: 'Paper', color: 'btn-warning', title: 'Launch Paper Trading', isBacktest: false },
  BACKTEST: { value: 'BACKTEST', label: 'Backtest', color: 'btn-info', title: 'Run Backtest', isBacktest: true },
};

const INTERVAL_OPTIONS = [
  { value: '1m', label: '1 Minute' },
  { value: '3m', label: '3 Minutes' },
  { value: '5m', label: '5 Minutes' },
  { value: '10m', label: '10 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '2h', label: '2 Hours' },
  { value: '3h', label: '3 Hours' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
  { value: '1M', label: '1 Month' },
];

// SVG Icons
const ChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
);

const CodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const DuplicateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
  </svg>
);

const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// Helper function to convert date (array or string) to Date object
const convertToDate = (dateValue) => {
  if (!dateValue) return null;

  // Handle array format: [year, month, day]
  if (Array.isArray(dateValue)) {
    return new Date(dateValue[0], dateValue[1] - 1, dateValue[2]);
  }

  // Handle string format: "2025-12-08" or ISO format
  if (typeof dateValue === 'string') {
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

// Helper function to format date for display
const formatDate = (dateValue) => {
  const date = convertToDate(dateValue);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (dateValue) => {
  const date = convertToDate(dateValue);
  if (!date) return 'N/A';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateInput = (dateValue) => {
  const date = convertToDate(dateValue);
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const dateInputToIso = (dateStr, { endOfDay } = { endOfDay: false }) => {
  if (!dateStr) return undefined;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return undefined;
  if (endOfDay) {
    d.setHours(23, 59, 59, 999);
  }
  return d.toISOString();
};

// Backward compatibility alias
const convertDateArrayToDate = convertToDate;

const StrategyList = ({ onSelectStrategy }) => {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter State
  const [searchInput, setSearchInput] = useState('');
  const [searchName, setSearchName] = useState('');
  const [idInput, setIdInput] = useState('');
  const [searchId, setSearchId] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState(null); // null=All, true=Public, false=Private

  // Server-side Pagination State
  const [currentPage, setCurrentPage] = useState(0); // zero-indexed for API
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [executionModal, setExecutionModal] = useState(null);
  const [executionForm, setExecutionForm] = useState({
    tickerName: '',
    interval: '1h',
    startDate: '',
    endDate: '',
    broker: 'UPSTOX',
    lotSize: null,
  });

  // Launch state
  const [launchingStrategy, setLaunchingStrategy] = useState(null);
  const [launchConfirm, setLaunchConfirm] = useState(null);
  const [activeBacktestJob, setActiveBacktestJob] = useState(null);
  const [launchNotification, setLaunchNotification] = useState(null);

  // Debounce timer ref
  const debounceRef = useRef(null);

  // Debounced search input handler
  const handleSearchInput = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchName(value);
      setCurrentPage(0);
    }, 400);
  };

  // Debounced strategy ID input handler
  const handleIdInput = (value) => {
    setIdInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchId(value);
      setCurrentPage(0);
    }, 400);
  };

  // Handle visibility filter change
  const handleVisibilityChange = (value) => {
    setVisibilityFilter(value);
    setCurrentPage(0);
  };

  const fetchStrategies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trendlineStrategyService.searchStrategies({
        strategyId: searchId || undefined,
        name: searchName || undefined,
        isPublic: visibilityFilter,
        page: currentPage,
        size: pageSize,
        sort: 'updatedAt,desc',
      });
      setStrategies(response.data || []);
      const meta = response.meta || {};
      setTotalPages(meta.totalPages || 0);
      setTotalItems(meta.totalItems || 0);
    } catch (err) {
      console.error('Error fetching strategies:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load strategies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchId, searchName, visibilityFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleRowClick = (strategy) => {
    if (onSelectStrategy) {
      onSelectStrategy(strategy);
    }
  };

  const handleViewJson = (e, strategy) => {
    e.stopPropagation();
    setSelectedStrategy(strategy);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStrategy(null);
  };

  const handleLaunchClick = (e, strategy, mode) => {
    e.stopPropagation();
    openExecutionModal(strategy, mode);
  };

  const openExecutionModal = (strategy, mode) => {
    const interval = strategy?.interval || '1h';
    const isBacktest = EXECUTION_MODES[mode]?.isBacktest;

    const today = new Date();
    const plus7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    setExecutionForm({
      tickerName: strategy?.tickerName || '',
      interval,
      startDate: isBacktest ? formatDateInput(strategy?.startDate) : formatDateInput(today),
      endDate: isBacktest ? formatDateInput(strategy?.endDate) : formatDateInput(plus7Days),
      broker: 'UPSTOX',
      lotSize: strategy?.lotSize || null, // Pre-fill from saved strategy
    });
    setExecutionModal({ strategy, mode });
  };

  const closeExecutionModal = () => {
    setExecutionModal(null);
  };

  const updateExecutionForm = (field, value) => {
    setExecutionForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRunExecution = () => {
    if (!executionModal?.strategy || !executionModal?.mode) {
      return;
    }

    const mode = executionModal.mode;
    const strategy = executionModal.strategy;

    if (!executionForm.tickerName || !executionForm.tickerName.trim()) {
      setLaunchNotification({ type: 'error', message: 'Ticker is required' });
      setTimeout(() => setLaunchNotification(null), 5000);
      return;
    }

    if (!executionForm.interval) {
      setLaunchNotification({ type: 'error', message: 'Interval is required' });
      setTimeout(() => setLaunchNotification(null), 5000);
      return;
    }

    const startDateIso = dateInputToIso(executionForm.startDate, { endOfDay: false });
    const endDateIso = dateInputToIso(executionForm.endDate, { endOfDay: true });

    if (!startDateIso || !endDateIso) {
      setLaunchNotification({ type: 'error', message: 'Start date and end date are required' });
      setTimeout(() => setLaunchNotification(null), 5000);
      return;
    }

    if (new Date(startDateIso).getTime() > new Date(endDateIso).getTime()) {
      setLaunchNotification({ type: 'error', message: 'Start date must be before end date' });
      setTimeout(() => setLaunchNotification(null), 5000);
      return;
    }

    const modeConfig = EXECUTION_MODES[mode];
    if (modeConfig?.isBacktest) {
      const params = {
        tickerName: executionForm.tickerName.trim(),
        interval: executionForm.interval,
        startDate: startDateIso,
        endDate: endDateIso,
        lotSize: executionForm.lotSize ? parseInt(executionForm.lotSize, 10) : undefined,
      };
      executeLaunch(strategy, mode, params);
      closeExecutionModal();
      return;
    }

    const params = {
      broker: executionForm.broker || 'UPSTOX',
      mode,
      interval: executionForm.interval,
      startDate: startDateIso,
      endDate: endDateIso,
    };

    if (mode === 'LIVE') {
      setLaunchConfirm({ strategy, mode, params });
      closeExecutionModal();
      return;
    }

    executeLaunch(strategy, mode, params);
    closeExecutionModal();
  };

  const executeLaunch = async (strategy, mode, overrideParams = null) => {
    setLaunchingStrategy({ id: strategy.id, mode });
    setLaunchConfirm(null);
    try {
      const modeConfig = EXECUTION_MODES[mode];

      if (modeConfig?.isBacktest) {
        // Backend fetches strategy by strategyId, so we only need execution params
        const params = overrideParams || {
          tickerName: executionForm.tickerName.trim(),
          interval: executionForm.interval || '1h',
          startDate: dateInputToIso(executionForm.startDate, { endOfDay: false }),
          endDate: dateInputToIso(executionForm.endDate, { endOfDay: true }),
        };

        // Submit async backtest job

        try {
          const job = await submitAsyncBacktest(strategy.id, strategy.name, params);
          // Open progress modal
          setActiveBacktestJob({
            ...job,
            strategyName: strategy.name,
          });
          setLaunchingStrategy(null);
          return; // Early return - modal handles the rest
        } catch (jobError) {
          // Check for rate limit error
          if (jobError.code === 'ALGO_JOB_004' || jobError.message?.includes('active backtests')) {
            setLaunchNotification({
              type: 'error',
              message: 'Maximum 5 concurrent backtests reached. Please wait for one to complete.'
            });
          } else {
            throw jobError; // Re-throw for outer catch
          }
          setLaunchingStrategy(null);
          return;
        }
      } else {
        const params = overrideParams || {
          broker: 'UPSTOX',
          mode: mode,
        };

        const response = await trendlineStrategyService.launch(strategy.id, params);
        const result = response.data;

        setLaunchNotification({
          type: 'success',
          message: `Strategy "${strategy.name}" launched in ${mode} mode! Execution ID: ${result?.executionId || 'N/A'}`
        });
      }
    } catch (err) {
      console.error('Error executing strategy:', err);
      setLaunchNotification({
        type: 'error',
        message: err.response?.data?.error?.detail || err.message || `Failed to execute strategy in ${mode} mode`
      });
    } finally {
      setLaunchingStrategy(null);
      // Auto-dismiss notification after 5 seconds
      setTimeout(() => setLaunchNotification(null), 5000);
    }
  };

  const handleConfirmLaunch = () => {
    if (launchConfirm) {
      executeLaunch(launchConfirm.strategy, launchConfirm.mode, launchConfirm.params || null);
    }
  };

  const handleCancelLaunch = () => {
    setLaunchConfirm(null);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center p-8 h-64">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4">
          <div className="alert alert-error">
            <span>{error}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setError(null)}>✕</button>
          </div>
        </div>
      );
    }

    if (strategies.length === 0) {
      return (
        <div className="p-12 text-center bg-base-100 rounded-lg border border-dashed border-base-300">
          <p className="text-lg font-medium text-base-content/60">No strategies found</p>
          <p className="text-sm text-base-content/40 mt-1">Create your first strategy to get started.</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto px-4">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th className="pl-0">Strategy ID</th>
              <th>Name</th>
              <th>Public</th>
              <th>Ticker</th>
              <th>Date Range</th>
              <th>Description</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Execution</th>
              <th className="pr-0 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {strategies.map((strategy) => (
              <tr
                key={strategy.id}
                className="hover:bg-base-200 cursor-pointer transition-colors h-16 group"
                onClick={() => handleRowClick(strategy)}
              >
                <td className="text-xs text-base-content/60 font-mono pl-0">{strategy.id}</td>
                <td className="font-medium">{strategy.name}</td>
                <td>
                  {strategy.public ? (
                    <span className="badge badge-success badge-sm text-white">Public</span>
                  ) : (
                    <span className="badge badge-ghost badge-sm">Private</span>
                  )}
                </td>
                <td>
                  {strategy.tickerName && (
                    <div className="badge badge-ghost gap-1">
                      <ChartIcon /> {strategy.tickerName}
                    </div>
                  )}
                </td>
                <td className="text-sm text-base-content/70">
                  {strategy.startDate && strategy.endDate ? (
                    `${formatDate(strategy.startDate)} - ${formatDate(strategy.endDate)}`
                  ) : <span className="italic text-base-content/30">N/A</span>}
                </td>
                <td className="max-w-xs truncate text-base-content/60" title={strategy.description}>
                  {strategy.description || <span className="italic text-base-content/30">No description</span>}
                </td>
                <td className="text-xs text-base-content/70 whitespace-nowrap">
                  {strategy.createdAt ? formatDateTime(strategy.createdAt) : <span className="italic text-base-content/30">N/A</span>}
                </td>
                <td className="text-xs text-base-content/70 whitespace-nowrap">
                  {strategy.updatedAt ? formatDateTime(strategy.updatedAt) : <span className="italic text-base-content/30">N/A</span>}
                </td>
                <td>
                  <div className="flex gap-1">
                    {Object.values(EXECUTION_MODES).map((mode) => {
                      const isLaunching = launchingStrategy?.id === strategy.id && launchingStrategy?.mode === mode.value;
                      return (
                        <button
                          key={mode.value}
                          className={`btn btn-xs ${mode.color} gap-1`}
                          onClick={(e) => handleLaunchClick(e, strategy, mode.value)}
                          disabled={isLaunching}
                          title={mode.title}
                        >
                          {isLaunching ? (
                            <span className="loading loading-spinner loading-xs"></span>
                          ) : (
                            <PlayIcon />
                          )}
                          {mode.label}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="pr-0 text-right">
                  <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      className="btn btn-square btn-ghost btn-sm"
                      onClick={(e) => { e.stopPropagation(); handleRowClick(strategy); }}
                      title="Copy to Builder"
                    >
                      <DuplicateIcon />
                    </button>
                    <button
                      className="btn btn-square btn-ghost btn-sm"
                      onClick={(e) => handleViewJson(e, strategy)}
                      title="View JSON"
                    >
                      <CodeIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="card card-bordered border-base-300 bg-base-100 shadow-sm">
        <div className="px-4 py-4 border-b border-base-200 bg-base-100 rounded-t-xl space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-lg">Strategy List</h3>
            <span className="badge badge-ghost">Total: {totalItems}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <input
                type="text"
                placeholder="Search by name..."
                className="input input-bordered input-sm w-full pl-9"
                value={searchInput}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Strategy ID..."
              className="input input-bordered input-sm w-56"
              value={idInput}
              onChange={(e) => handleIdInput(e.target.value)}
            />
            <div className="join">
              <button
                className={`join-item btn btn-sm ${visibilityFilter === null ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => handleVisibilityChange(null)}
              >
                All
              </button>
              <button
                className={`join-item btn btn-sm ${visibilityFilter === true ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => handleVisibilityChange(true)}
              >
                Public
              </button>
              <button
                className={`join-item btn btn-sm ${visibilityFilter === false ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => handleVisibilityChange(false)}
              >
                Private
              </button>
            </div>
          </div>
        </div>

        {renderContent()}

        {/* Pagination Controls */}
        {!loading && totalPages > 0 && (
          <div className="p-4 flex justify-center border-t border-base-200 bg-base-100 rounded-b-xl">
            <div className="join">
              <button
                className="join-item btn btn-sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
              >
                <ChevronLeftIcon />
              </button>
              <button className="join-item btn btn-sm no-animation cursor-default bg-base-100">
                Page {currentPage + 1} of {totalPages}
              </button>
              <button
                className="join-item btn btn-sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* JSON Viewer Modal */}
      {isModalOpen && selectedStrategy && createPortal(
        <dialog className="modal modal-open">
          <div className="modal-box w-11/12 max-w-5xl h-[85vh] p-0 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-base-300 bg-base-100">
              <div>
                <h3 className="font-bold text-lg">{selectedStrategy.name}</h3>
                <p className="text-xs text-base-content/60 font-mono mt-1">{selectedStrategy.id}</p>
              </div>
              <button className="btn btn-sm btn-circle btn-ghost" onClick={handleCloseModal}>
                <CloseIcon />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 bg-base-200">
              <pre className="p-4 bg-base-100 rounded-lg text-sm font-mono whitespace-pre-wrap break-words border border-base-300 shadow-inner text-base-content">
                <code>{JSON.stringify(selectedStrategy, null, 2)}</code>
              </pre>
            </div>

            <div className="p-4 border-t border-base-300 bg-base-100 flex justify-end">
              <button className="btn" onClick={handleCloseModal}>Close</button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseModal}>close</button>
          </form>
        </dialog>,
        document.body
      )}

      {executionModal && executionModal.strategy && createPortal(
        <dialog className="modal modal-open">
          <div className="modal-box w-11/12 max-w-2xl p-0 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-base-300 bg-base-100">
              <div>
                <h3 className="font-bold text-lg">{EXECUTION_MODES[executionModal.mode]?.label} Execution</h3>
                <p className="text-xs text-base-content/60 font-mono mt-1">{executionModal.strategy.name}</p>
              </div>
              <button className="btn btn-sm btn-circle btn-ghost" onClick={closeExecutionModal}>
                <CloseIcon />
              </button>
            </div>

            <div className="p-4 bg-base-100 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Ticker</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered bg-base-200 w-full"
                    value={executionForm.tickerName}
                    onChange={(e) => updateExecutionForm('tickerName', e.target.value.toUpperCase())}
                    placeholder="e.g. RELIANCE"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Interval</span>
                  </label>
                  <select
                    className="select select-bordered bg-base-200 w-full"
                    value={executionForm.interval}
                    onChange={(e) => updateExecutionForm('interval', e.target.value)}
                  >
                    {INTERVAL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Start Date</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered bg-base-200 w-full"
                    value={executionForm.startDate}
                    onChange={(e) => updateExecutionForm('startDate', e.target.value)}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">End Date</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered bg-base-200 w-full"
                    value={executionForm.endDate}
                    onChange={(e) => updateExecutionForm('endDate', e.target.value)}
                  />
                </div>
              </div>

              {EXECUTION_MODES[executionModal.mode]?.isBacktest && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Lot Size</span>
                      <span className="label-text-alt text-base-content/50">Units per signal</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="input input-bordered bg-base-200 w-full"
                      value={executionForm.lotSize ?? ''}
                      onChange={(e) => updateExecutionForm('lotSize', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      placeholder="Default: 1"
                    />
                    <label className="label">
                      <span className="label-text-alt text-base-content/50">Optional. Overrides strategy lot size.</span>
                    </label>
                  </div>
                </div>
              )}

              {!EXECUTION_MODES[executionModal.mode]?.isBacktest && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Broker</span>
                    </label>
                    <select
                      className="select select-bordered bg-base-200 w-full"
                      value={executionForm.broker}
                      onChange={(e) => updateExecutionForm('broker', e.target.value)}
                    >
                      <option value="UPSTOX">UPSTOX</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-base-300 bg-base-100 flex justify-end gap-2">
              <button className="btn" onClick={closeExecutionModal}>Cancel</button>
              <button
                className={`btn ${EXECUTION_MODES[executionModal.mode]?.color || 'btn-primary'}`}
                onClick={handleRunExecution}
              >
                Run
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={closeExecutionModal}>close</button>
          </form>
        </dialog>,
        document.body
      )}

      {/* Launch Confirmation Modal for LIVE mode */}
      {launchConfirm && createPortal(
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-error">⚠️ Confirm Live Trading</h3>
            <p className="py-4">
              You are about to launch <strong>"{launchConfirm.strategy.name}"</strong> in <strong className="text-error">LIVE</strong> mode.
              This will execute real trades with real money.
            </p>
            <p className="text-sm text-base-content/70">
              Are you sure you want to proceed?
            </p>
            <div className="modal-action">
              <button className="btn" onClick={handleCancelLaunch}>Cancel</button>
              <button className="btn btn-error" onClick={handleConfirmLaunch}>Launch Live</button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCancelLaunch}>close</button>
          </form>
        </dialog>,
        document.body
      )}

      {/* Launch Notification Toast */}
      {launchNotification && createPortal(
        <div className="toast toast-top toast-end z-50">
          <div className={`alert ${launchNotification.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{launchNotification.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setLaunchNotification(null)}>✕</button>
          </div>
        </div>,
        document.body
      )}

      {/* Backtest Progress Modal */}
      {activeBacktestJob && (
        <BacktestProgressModal
          job={activeBacktestJob}
          onClose={() => setActiveBacktestJob(null)}
          onComplete={(resultId) => {
            console.log('Backtest completed, result ID:', resultId);
          }}
        />
      )}
    </div>
  );
};

export default StrategyList;
